import { Garage, Organization, Service } from "@App/types";
import { FuelType } from "@App/types/ayvens/fuelType";
import { SortOrderType } from "@App/types/enums";
import {
  GarageHasNationwideService,
  GarageHasPhone,
  GarageIsBookable,
  GarageIsDrivinLicenseCheckpoint,
} from "@App/utils/conditionals/garages";
import {
  OrganizationIsAkf,
  OrganizationIsAkfLangzeitmiete,
  OrganizationIsArval,
  OrganizationIsAthlon,
  OrganizationIsDBConnect,
  OrganizationIsDeutscheLeasing,
  OrganizationIsFinn,
  OrganizationIsFleethub,
  OrganizationIsAyvens,
  OrganizationIsMeinAuto,
  OrganizationIsMobileGarantie,
  OrganizationIsMobilityConcept,
  OrganizationIsNio,
  OrganizationIsItzehoer,
  OrganizationIsRaiffeisen,
  OrganizationIsRepareo,
  OrganizationIsSignalIduna,
} from "@App/utils/conditionals/organization";
import {
  ServiceIsAppointment,
  ServiceIsAutoAbo,
  ServiceIsCertificate,
  ServiceIsEVehicleAppointment,
  ServiceIsFlatTireDrivable,
  ServiceIsGaragesNearby,
  ServiceIsGlass,
  ServiceIsInspection,
  ServiceIsTireStorage,
  ServiceIsVehicleReturn,
  ServiceIsWheelChange,
  ServiceIsWheelChangeTiresStored,
  ServiceIsWheelReplacement,
} from "@App/utils/conditionals/services";

export const GarageIsBookableOnArval = (organization: Organization | null, garage: Garage) => {
  return GarageIsBookable(garage) && OrganizationIsArval(organization);
};

export const ServiceIsInspectionOrServiceIsAppointment = (service: Service | null) => {
  return ServiceIsInspection(service) || ServiceIsAppointment(service);
};

export const ServiceIsInspectionOrServiceIsAppointmentOrServiceIsCertificate = (
  service: Service | null,
) => {
  return (
    ServiceIsInspection(service) || ServiceIsAppointment(service) || ServiceIsCertificate(service)
  );
};

export const ServiceIsInspectionOrServiceIsAppointmentOrServiceIsEVehicleAppointment = (
  service: Service | null,
) => {
  return (
    ServiceIsInspection(service) ||
    ServiceIsAppointment(service) ||
    ServiceIsEVehicleAppointment(service)
  );
};

export const OrganizationIsNeitherRepareoNorDBConnect = (organization: Organization | null) => {
  return !OrganizationIsDBConnect(organization) && !OrganizationIsRepareo(organization);
};

export const OrganizationIsRepareoAndServiceIsInspection = (
  organization: Organization,
  service: Service,
) => {
  return ServiceIsInspection(service) && OrganizationIsRepareo(organization);
};

export const OrganizationIsAyvensAndServiceIsAppointment = (
  organization: Organization | null,
  service: Service,
) => {
  return (
    (ServiceIsAppointment(service) || ServiceIsEVehicleAppointment(service)) &&
    OrganizationIsAyvens(organization)
  );
};

export const ServiceIsGlassAndGarageHasNationwideService = (service: Service, garage: Garage) => {
  return ServiceIsGlass(service) && GarageHasNationwideService(garage);
};

export const ServiceIsVehicleReturnAndGarageIsDrivinLicenseCheckpoint = (
  service: Service,
  garage: Garage,
) => {
  return ServiceIsVehicleReturn(service) && GarageIsDrivinLicenseCheckpoint(garage);
};

export const OrganizationIsAyvensAndServiceIsGaragesnearby = (
  organization: Organization | null,
  service: Service,
) => {
  return ServiceIsGaragesNearby(service) && OrganizationIsAyvens(organization);
};

export const OrganizationIsNotDBConnectAndServiceIsVehicleReturn = (
  organization: Organization | null,
  service: Service,
) => {
  return ServiceIsVehicleReturn(service) && !OrganizationIsDBConnect(organization);
};

export const GarageIsNotBookableAndHasPhone = (garage: Garage) => {
  return !GarageIsBookable(garage) && GarageHasPhone(garage);
};

export const OrganizationIsArvalAndServiceIsWheelChange = (
  organization: Organization | null,
  service: Service,
) => {
  return ServiceIsWheelChange(service) && OrganizationIsArval(organization);
};

export const OrganizationIsNotArvalOrServiceIsNotWheelChange = (
  organization: Organization | null,
  service: Service,
) => {
  return !ServiceIsWheelChange(service) || !OrganizationIsArval(organization);
};

export const OrganizationIsAyvensAndServiceIsNotWheelchangeAndServiceIsNotGlass = (
  organization: Organization | null,
  service: Service,
) => {
  return (
    !ServiceIsWheelChange(service) && !ServiceIsGlass(service) && OrganizationIsAyvens(organization)
  );
};

export const OrganizationIsAyvensOrDBConnect = (organization: Organization | null) => {
  return OrganizationIsDBConnect(organization) || OrganizationIsAyvens(organization);
};

export const OrganizationIsArvalOrMobilityConcept = (organization: Organization | null) => {
  return OrganizationIsArval(organization) || OrganizationIsMobilityConcept(organization);
};

export const OrganizationIsAthlonAndSortByIsPartnerRecommended = (
  organization: Organization | null,
  sortBy: SortOrderType,
) => {
  return OrganizationIsAthlon(organization) && sortBy === SortOrderType.PARTNER_RECOMMENDED;
};

export const ServiceIsWheelChangeOrServieIsWheelChangeTiresstored = (service: Service | null) => {
  return ServiceIsWheelChange(service) || ServiceIsWheelChangeTiresStored(service);
};

export const ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored = (
  service: Service | null,
) => {
  return (
    ServiceIsWheelChange(service) ||
    ServiceIsAutoAbo(service) ||
    ServiceIsWheelChangeTiresStored(service) ||
    ServiceIsTireStorage(service)
  );
};

export const OrganizationIsMobilityConceptAndServiceIsAutoAbo = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsMobilityConcept(organization) && ServiceIsAutoAbo(service);
};

export const OrganizationIsAyvensAndServiceIsEVehicleAppointment = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsAyvens(organization) && ServiceIsEVehicleAppointment(service);
};

export const OrganizationHasSelectableBevSupport = (organization: Organization | null) => {
  return (
    OrganizationIsAyvens(organization) ||
    OrganizationIsArval(organization) ||
    OrganizationIsFinn(organization)
  );
};

export const ServiceShouldDisplayBevOnCurrentList = (
  service: Service,
  organization: Organization | null,
) => {
  return (
    (!ServiceIsGlass(service) && OrganizationIsAyvens(organization)) ||
    (OrganizationIsFinn(organization) && !ServiceIsFlatTireDrivable(service)) ||
    (OrganizationIsArval(organization) && ServiceIsInspection(service))
  );
};

export const OrganizationIsNotAkfAndNotAkfLangzeitmiete = (organization: Organization | null) => {
  return !OrganizationIsAkf(organization) && !OrganizationIsAkfLangzeitmiete(organization);
};

export const ServiceIsWheelChangeAndOrganizationIsNio = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsNio(organization) && ServiceIsWheelChange(service);
};

export const ServiceIsWheelReplacementAndOrganizationIsNio = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsNio(organization) && ServiceIsWheelReplacement(service);
};

export const ServiceIsWheelChangeTiresstoredAndOrganizationIsNio = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsNio(organization) && ServiceIsWheelChangeTiresStored(service);
};

export const ServiceIsWheelChangeAndOrganizationIsMeinAuto = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsMeinAuto(organization) && ServiceIsWheelChange(service);
};

export const ServiceIsWheelReplacementAndOrganizationIsMeinAuto = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsMeinAuto(organization) && ServiceIsWheelReplacement(service);
};

export const ServiceIsWheelRelatedAndOrganizationIsMeinAuto = (
  organization: Organization | null,
  service: Service,
) => {
  return (
    OrganizationIsMeinAuto(organization) &&
    (ServiceIsWheelReplacement(service) ||
      ServiceIsWheelChange(service) ||
      ServiceIsWheelChangeTiresStored(service))
  );
};

export const ServiceIsWheelRelatedAndOrganizationIsMobilityConcept = (
  organization: Organization | null,
  service: Service,
) => {
  return (
    OrganizationIsMobilityConcept(organization) &&
    (ServiceIsWheelReplacement(service) ||
      ServiceIsWheelChange(service) ||
      ServiceIsWheelChangeTiresStored(service))
  );
};

export const ServiceIsWheelReplacementAndOrganizationIsMobilityConcept = (
  organization: Organization | null,
  service: Service,
) => {
  return OrganizationIsMobilityConcept(organization) && ServiceIsWheelReplacement(service);
};

export const OrganizationShouldDisplayAsNonBookableForTires = (
  organization: Organization | null,
) => {
  return (
    OrganizationIsAkf(organization) ||
    OrganizationIsAkfLangzeitmiete(organization) ||
    OrganizationIsDeutscheLeasing(organization) ||
    OrganizationIsArval(organization) ||
    OrganizationIsDBConnect(organization) ||
    OrganizationIsFleethub(organization) ||
    OrganizationIsAthlon(organization) ||
    OrganizationIsRaiffeisen(organization) ||
    OrganizationIsMobileGarantie(organization)
  );
};

export const OrganizationIsSignalIdunaOrItzehoerAndServiceIsGlass = (
  organization: Organization | null,
  service: Service | null,
) => {
  return (
    (OrganizationIsSignalIduna(organization) || OrganizationIsItzehoer(organization)) &&
    ServiceIsGlass(service)
  );
};

export const fuelTypeIsBevForAyvens = (id: number) => {
  return (
    id === FuelType.BENZINELEKTRO ||
    id === FuelType.DIESELELEKTRO ||
    id === FuelType.ELEKTRO ||
    id === FuelType.WASSERSTOFF ||
    id === FuelType.BENZINETHANOLELEKTRO
  );
};
